import { assertCurrentBrowserSession, checkBrowserSessionResponse } from "@/lib/browser-session";
import { decodeFirebaseJwt } from "@/lib/jwt-decode";

import { getAuthToken } from "../auth";
import { shopAgentUrl } from "../shared";

import type { CreateClientConfig } from "./client/client";

/**
 * Client configuration for the Shop Agent API
 *
 * Auth behavior is determined by the API host (NEXT_PUBLIC_AGENT_API_HOST):
 *
 * Production/Gateway mode (api.storerouter.ai or legacy api.octogen.ai):
 * - Sends Firebase JWT in Authorization header
 * - EnvoyGateway validates JWT and sets X-Octogen-User-ID header
 *
 * Local backend mode (localhost):
 * - Decodes Firebase JWT client-side to extract user ID
 * - Sets X-Octogen-User-ID header directly (backend reads this header)
 * - Also sends Authorization header for consistency
 *
 * The backend is auth-agnostic - it only reads the X-Octogen-User-ID header.
 */

/**
 * Determines if the API host is a local backend (localhost or 127.0.0.1)
 */
function isLocalBackend(apiUrl: string): boolean {
  try {
    const url = new URL(apiUrl);
    return url.hostname === "localhost" || url.hostname === "127.0.0.1";
  } catch {
    // If URL parsing fails, assume it's not local
    return false;
  }
}

export const createClientConfig: CreateClientConfig = (config) => {
  const baseUrl = shopAgentUrl();
  const useLocalAuthMode = isLocalBackend(baseUrl);

  const baseConfig = {
    ...config,
    baseUrl,
  };

  return {
    ...baseConfig,
    async fetch(url: RequestInfo | URL, init?: RequestInit): Promise<Response> {
      // Get the Firebase auth token
      const token = await getAuthToken();

      const uid = token ? decodeFirebaseJwt(token)?.uid : undefined;
      assertCurrentBrowserSession(uid);

      // Build headers
      const headers = new Headers(init?.headers || {});

      if (token) {
        // Always set Authorization header
        headers.set("Authorization", `Bearer ${token}`);

        // For local backend, also set X-Octogen-User-ID header directly
        // This allows the backend to work without EnvoyGateway
        if (useLocalAuthMode) {
          const decoded = decodeFirebaseJwt(token);
          if (decoded?.uid) {
            headers.set("X-Octogen-User-ID", decoded.uid);
          } else {
            console.warn(
              "Local auth mode: Failed to decode Firebase JWT - X-Octogen-User-ID header not set. " +
                "API calls will fail with 401. Check that the token is a valid Firebase JWT."
            );
          }
        }
      }

      // Call the native fetch with updated headers
      const fetchFn = baseConfig.fetch || globalThis.fetch;
      const response = await fetchFn(url, {
        ...init,
        headers,
      });
      return checkBrowserSessionResponse(response, uid);
    },
  };
};
